import { ActionSheetContext, ActionSheetV2, Icon, ListItem } from '@alto/design-system';
import React, { useContext } from 'react';
import getAllowPriceOptionChange from '~shared/features/checkout/selectors/getAllowPriceOptionChange';
import { getSelectedPrescription } from '~shared/features/prescriptions/selectors/getSelectedPrescription';
import { getCurrentUserIsEligibleForQuantityChange } from '~shared/features/users/selectors/getUserIsEligibleForQuantityChange';
import { useSelectorShared } from '~shared/store';
import { AutoRefillToggle, ORDER_TYPE_CONTEXT, type OrderTypeContext } from '../../auto-refill';
import { useCanChangeAutoRefillStatus } from '../../auto-refill/hooks/useCanChangeAutoRefillStatus';
import { useCanRemoveMedication } from '../hooks/useCanRemoveMedication';
import { ChangeMedicationItemQuantity } from './ChangeMedicationItemQuantity';
import { PriceOptionChangeListItem } from './PriceOptionChangeListItem';

type EditMedicationActionSheetProps = {
  readonly onRemoveMedication: (() => Promise<void>) | (() => void);
  readonly onClose?: () => void;
  readonly orderTypeContext: OrderTypeContext;
};

export const EditMedicationActionSheet = ({
  onRemoveMedication,
  onClose,
  orderTypeContext,
}: EditMedicationActionSheetProps) => {
  const isPostCheckout = orderTypeContext === ORDER_TYPE_CONTEXT.POST_CHECKOUT;
  const { closeActionSheet } = useContext(ActionSheetContext);
  const prescription = useSelectorShared(getSelectedPrescription);
  const prescriptionID = prescription?.id;
  const allowPriceOptionChange = useSelectorShared((state) =>
    prescriptionID ? getAllowPriceOptionChange(state, { prescriptionID }) : false,
  );
  const userIsEligibleForQuantityChange = useSelectorShared(getCurrentUserIsEligibleForQuantityChange);
  const { canChangeAutoRefillStatus } = useCanChangeAutoRefillStatus({ isPostCheckout });
  const { canRemoveMedication } = useCanRemoveMedication({ orderTypeContext });

  if (!prescription) return null;

  const handleClose = () => {
    closeActionSheet();
    onClose?.();
  };

  return (
    <ActionSheetV2
      title={`Edit ${prescription?.medication_name}`}
      handleClose={handleClose}
      analyticsName="checkout edit medication"
    >
      {allowPriceOptionChange && !isPostCheckout ? (
        <PriceOptionChangeListItem
          prescriptionID={prescription.id}
          handleClose={handleClose}
        />
      ) : null}
      {userIsEligibleForQuantityChange && !isPostCheckout ? (
        <ChangeMedicationItemQuantity
          prescription={prescription}
          handleClose={handleClose}
        />
      ) : null}
      {canChangeAutoRefillStatus ? (
        <AutoRefillToggle
          handleToggleAutoRefill={() => {
            // timeout to allow the switch to animate, then close the action sheet
            setTimeout(handleClose, 300);
          }}
          orderTypeContext={orderTypeContext}
          prescription={prescription}
        />
      ) : null}
      {canRemoveMedication ? (
        <ListItem
          title={isPostCheckout ? 'Remove from order' : 'Remove from cart'}
          LeftContent={<Icon name="trash" />}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onPress={onRemoveMedication}
        />
      ) : null}
    </ActionSheetV2>
  );
};
